import React, {
  createContext,
  useMemo,
  ReactElement,
  useState,
  useCallback
} from 'react';
import CreateProjectImage from '@assets/images/tour/create-project.png';
import RouteForApprovalImage from '@assets/images/tour/route-for-approval.png';
import UploadMediaImage from '@assets/images/tour/upload-media.png';
import ReviewMediaImage from '@assets/images/tour/review-media.png';
import TurnFeedbackIntoTasksImage from '@assets/images/tour/turn-feedback-into-tasks.png';
import ReviewDocumentsImage from '@assets/images/tour/review-documents.png';
import TimeRangeFeedbackFinalImage from '@assets/images/tour/time-range-feedback-final.png';
import { User } from '@context/AuthContext/types';
import { completeOnboardingStep } from '@api/Profile';
import { CompleteOnboardingStepDto } from '@api/Api';
import AppTour from '@components/AppTour/AppTour';
import { useAuth } from '@hooks/useAuth';
import OnboardingProcess from '@components/OnboardingProcess/OnboardingProcess';

export type OnboardingStepsType =
  | CompleteOnboardingStepDto['type']
  | 'intro'
  | 'tour-paused'
  | 'tour-paused-media-viewer'
  | 'announcement-media-viewer'
  | 'announcement-media-viewer-mobile';

export type OnboardingTheme = 'dark' | 'white' | 'lime-green';

export type TourValue = {
  type: OnboardingStepsType | undefined;
  theme?: OnboardingTheme;
  onClickNext?: (currentStep: number, isExitTour: boolean) => void;
  onClickPrev?: (currentStep: number, isExitTour: boolean) => void;
  action?: (currentStep: number, node: any) => void;
  onClose?: (currentStep: number, isExitTour: boolean) => void;
};

interface IOnboardingProcessContext {
  onCompleteStep: ({
    type,
    targetId,
    hideCongrats
  }: {
    type: OnboardingStepsType;
    targetId?: string | null;
    hideCongrats?: boolean;
  }) => void;
  setGoToStep: (v: number) => void;
  onStartTour: (data: TourValue) => void;
  user: User<'external-reviewer' | 'internal'> | null;
  showProcess: OnboardingStepsType | false;
  tourData: TourValue | undefined;
  goToStep: number;
}

export const OnboardingProcessContext =
  createContext<IOnboardingProcessContext>({
    onCompleteStep: () => undefined,
    onStartTour: () => undefined,
    setGoToStep: () => undefined,
    user: null,
    showProcess: false,
    tourData: undefined
  } as any);

export type StepCopyItem = {
  title: string;
  description: string;
  button: string;
  image?: string;
  steps?: string;
  isShowConfetti?: boolean;
};

const stepCopies: {
  [index: string]: StepCopyItem;
} = {
  'create-campaign': {
    title: "That's it!",
    description:
      'Your project is ready to go. Start uploading media and collaborating with your team.',
    image: CreateProjectImage,
    button: 'Continue onboarding',
    isShowConfetti: false
  },
  'upload-asset': {
    title: 'Easy as pie!',
    description:
      'You’ve got uploading down! Explore more onboarding steps to get the most out of StreamWork.',
    image: UploadMediaImage,
    button: 'Continue onboarding',
    isShowConfetti: false
  },
  'review-asset': {
    title: 'Nice work!',
    description:
      'You’re ready to jump in, review media, and keep your projects moving.',
    image: ReviewMediaImage,
    button: 'Continue onboarding'
  },
  'create-task-from-comment': {
    title: 'Task mastered!',
    description:
      'You’ve turned a comment into a task — now everything’s trackable in one spot.',
    button: 'Continue onboarding',
    image: TurnFeedbackIntoTasksImage,
    isShowConfetti: false
  },
  'text-highlight': {
    title: 'All done!',
    description: 'Now you can view and edit documents like a StreamWork pro!',
    button: 'Continue onboarding',
    image: ReviewDocumentsImage,
    isShowConfetti: false
  },
  'review-audio-video': {
    title: "You're all set!",
    description:
      'Start reviewing, marking up, and approving media shared with you.',
    button: 'Continue onboarding',
    image: TimeRangeFeedbackFinalImage,
    isShowConfetti: false
  },
  'route-for-approval': {
    title: 'Congrats!',
    description: 'You’ve successfully created your first approval workflow.',
    button: 'Continue onboarding',
    image: RouteForApprovalImage,
    isShowConfetti: false
  }
};

type OnboardingProcessContextProps = {
  children: ReactElement | ReactElement[];
};

export function OnboardingProcessProvider({
  children
}: OnboardingProcessContextProps) {
  const [tourData, setTourData] = useState<TourValue | undefined>();
  const [goToStep, setGoToStep] = useState<number>(0);
  const { user, updateUserProfile } = useAuth(false);
  const [showProcess, setShowProcess] = useState<OnboardingStepsType | false>(
    false
  );

  const onOpenProcess = useCallback((type: OnboardingStepsType) => {
    setShowProcess(type);
  }, []);

  const onCloseProcess = useCallback(() => {
    setShowProcess(false);
  }, []);

  const onCompleteStep = useCallback(
    ({
      type,
      hideCongrats
    }: {
      type: OnboardingStepsType;
      hideCongrats?: boolean;
    }) => {
      const _type = type;
      if (!user?.completedOnboardingSteps) return;
      if (
        _type !== 'intro' &&
        _type !== 'tour-paused' &&
        _type !== 'tour-paused-media-viewer' &&
        _type !== 'announcement-media-viewer' &&
        _type !== 'announcement-media-viewer-mobile'
      ) {
        if (!user.completedOnboardingSteps.includes(_type)) {
          updateUserProfile({
            completedOnboardingSteps: [...user.completedOnboardingSteps, _type]
          });
          if (user.type === 'internal') completeOnboardingStep({ type: _type });
        }
      }
      if (!hideCongrats) onOpenProcess(type);
    },
    [
      onOpenProcess,
      updateUserProfile,
      user?.completedOnboardingSteps,
      user?.type
    ]
  );

  const onStartTour = useCallback(
    (data: TourValue) => {
      if (!data?.type) {
        setGoToStep(0);
        setTourData(undefined);
        return;
      }
      setTourData((prevTourData) => {
        if (prevTourData?.type !== data?.type) setGoToStep(0);
        return data;
      });
    },
    [setGoToStep, setTourData]
  );

  const providerValue = useMemo(() => {
    return {
      tourData,
      user,
      showProcess,
      goToStep,
      onCompleteStep,
      onStartTour,
      setGoToStep
    };
  }, [tourData, goToStep, user, showProcess, onCompleteStep, onStartTour]);

  const propsValue = useMemo(() => {
    return {
      user,
      copies: showProcess ? stepCopies[showProcess] : undefined,
      onCloseProcess,
      onStartTour
    };
  }, [onCloseProcess, user, showProcess, onStartTour]);

  return (
    <OnboardingProcessContext.Provider value={providerValue}>
      {showProcess && <OnboardingProcess {...propsValue} />}
      {children}
      <AppTour
        goToStep={goToStep}
        setGoToStep={setGoToStep}
        tourData={tourData}
        setTourData={setTourData}
        onCompleteStep={onCompleteStep}
      />
    </OnboardingProcessContext.Provider>
  );
}

export default OnboardingProcessProvider;
